// Sticky top navigation bar used by the Layout shell.
//
// Phase 1 keeps the nav set intentionally small. T029 wired Dashboard;
// T030 added Projects. T034 added Rules. The list lives here as a single
// `navItems` array so adding a primary route stays a one-line change.

import { FormEvent, useEffect, useRef, useState } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { LogOut, Search } from 'lucide-react';
import { WordmarkBase } from './Wordmark';
import { ToolSwitcher } from './ToolSwitcher';
import { LicenseBadge } from './LicenseBadge';
import { ApiStatusBadge } from './ApiStatusBadge';
import { decodeJWTClaims, signOut } from '../lib/auth';

interface NavItem {
  to: string;
  label: string;
  /**
   * react-router's `end` flag — only the root link wants exact-match
   * highlighting; nested routes like /projects/:id should still flag
   * "Projects" as active.
   */
  end?: boolean;
  /** Minimum role required to display this item (undefined → always shown). */
  minRole?: string;
}

const ROLE_RANK: Record<string, number> = {
  reader: 0,
  developer: 1,
  maintainer: 2,
  project_admin: 3,
  org_admin: 4,
};

const ALL_NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Dashboard', end: true },
  { to: '/projects', label: 'Projects' },
  { to: '/portfolios', label: 'Bundles' },
  { to: '/reports', label: 'Reports' },
  { to: '/rules', label: 'Rules' },
  // Admin entry point — visible to project_admin and above. The AdminShell
  // left-rail handles sub-navigation; the top nav only needs a single entry.
  { to: '/admin/settings', label: 'Admin', minRole: 'project_admin' },
];

export function TopNav() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');

  const role = decodeJWTClaims()?.role;
  const navItems = ALL_NAV_ITEMS.filter(({ minRole }) => {
    if (!minRole) return true;
    // Show when role is unknown (no JWT) as a fallback; backend enforces access.
    if (role === undefined) return true;
    return (ROLE_RANK[role] ?? -1) >= (ROLE_RANK[minRole] ?? 999);
  });

  // Press "/" anywhere (outside inputs/textareas) to focus the search box.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        e.key === '/' &&
        target.tagName !== 'INPUT' &&
        target.tagName !== 'TEXTAREA' &&
        !target.isContentEditable
      ) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    globalThis.addEventListener('keydown', handler);
    return () => globalThis.removeEventListener('keydown', handler);
  }, []);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    navigate(`/search?${p.toString()}`);
    setQuery('');
    inputRef.current?.blur();
  };

  return (
    <header
      className="
        sticky top-0 z-40 h-14 flex items-center gap-4 px-6
        border-b border-border bg-bg-base/80 backdrop-blur-sm
      "
    >
      {/* Wordmark: SVG + "ORBITAL/" is a home link; "LINT" is the tool
          switcher (lives outside the <a> — button-in-anchor is invalid HTML).

          `orb-wordmark` MUST sit on this wrapper, not only on WordmarkBase.
          It carries the brand typography (Outfit, 15px, uppercase,
          tracking-[0.14em], leading-none) and those properties are inherited.
          T296 moved "LINT" out of the Link into its sibling ToolSwitcher,
          which put it outside the old `.orb-wordmark` element — so its
          `text-[inherit] font-[inherit] tracking-[inherit]` silently resolved
          against the <header> instead: Inter at 14px with no letter-spacing.
          The result was a visibly mismatched suffix whose different line-height
          also broke the baseline, making the "/" look like it collided with the
          text. Keeping the class here puts both children back in one
          typographic context. */}
      <div className="orb-wordmark flex items-center shrink-0">
        <Link
          to="/"
          className="flex items-center hover:opacity-80 transition-opacity"
          aria-label="Orbital home"
        >
          <WordmarkBase />
        </Link>
        <ToolSwitcher />
      </div>

      <nav className="flex items-center gap-1 shrink-0" aria-label="Primary">
        {navItems.map(({ to, label, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              [
                'orb-nav-label px-3 py-1.5 rounded-md transition-colors',
                isActive
                  ? 'bg-accent/10 text-accent border border-accent/30'
                  : 'text-fg-muted hover:text-fg hover:bg-bg-hover border border-transparent',
              ].join(' ')
            }
          >
            {label}
          </NavLink>
        ))}
      </nav>

      {/* Global search bar — submits to /search?q=… */}
      <form
        onSubmit={handleSearch}
        role="search"
        aria-label="Global issue search"
        className="flex-1 flex justify-center"
      >
        <div className="relative w-full max-w-sm">
          <Search
            className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-fg-subtle pointer-events-none"
            aria-hidden="true"
          />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='Search issues & projects… ("/" to focus)'
            aria-label="Search issues and projects"
            className="
              w-full pl-8 pr-3 py-1.5 rounded-md border border-border
              bg-bg-inset text-fg text-xs placeholder-fg-subtle
              focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/50
              transition-colors
            "
          />
        </div>
      </form>

      <div className="flex items-center gap-3 shrink-0">
        <ApiStatusBadge />
        <LicenseBadge />
        <button
          type="button"
          onClick={() => signOut()}
          className="
            inline-flex items-center gap-2 px-3 py-1.5 rounded-md
            text-xs font-semibold uppercase tracking-[0.08em]
            text-fg-muted hover:text-fg hover:bg-bg-hover
            border border-border hover:border-border-strong
            transition-colors
          "
          aria-label="Sign out"
        >
          <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
          <span>Sign out</span>
        </button>
      </div>
    </header>
  );
}
